import React from 'react';
import { motion } from 'framer-motion';
import { Document, Page, pdfjs } from 'react-pdf';
import { FileText, Eye } from 'lucide-react';

// Set workerSrc for pdfjs
pdfjs.GlobalWorkerOptions.workerSrc = 
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.8.69/pdf.worker.min.mjs';

const PitchDeckSection = ({ company, handlePreview }) => {
  if (!company.pitchDeck?.uploaded || !company.pitchDeck?.url) {
    return null;
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-10 shadow-2xl mb-8">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
        <FileText className="w-6 h-6 text-purple-500" /> Pitch Deck
      </h2>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white rounded-xl shadow-sm">
            <FileText className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-900">{company.pitchDeck.fileName}</h3>
            <p className="text-xs text-gray-500">
              {company.pitchDeck.slides} slides • Last updated {new Date(company.pitchDeck.lastUpdated).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* PDF First Page Preview */}
      <div className="mt-4 sm:mt-6">
        {/* Mobile Preview */}
        <div className="flex justify-center md:hidden">
          <div 
            className="relative bg-white border-2 border-gray-200 rounded-lg shadow-md overflow-hidden cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all duration-200 group"
            onClick={handlePreview}
          >
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 z-10 flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white bg-opacity-90 rounded-full p-3">
                <Eye className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            
            <Document 
              file={company.pitchDeck.url} 
              loading={
                <div className="flex items-center justify-center bg-gray-50 rounded-lg w-[300px] h-[400px]">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <div className="text-sm text-gray-500">Loading preview...</div>
                  </div>
                </div>
              }
              error={
                <div className="flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 w-[300px] h-[400px]">
                  <div className="text-center text-gray-500 p-4">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <div className="text-sm">Preview unavailable</div>
                    <div className="text-xs mt-1">Click to open full PDF</div>
                  </div>
                </div>
              }
            >
              <Page 
                pageNumber={1} 
                width={300}
                renderTextLayer={false} 
                renderAnnotationLayer={false}
                className="shadow-sm"
              />
            </Document>
            
            {/* Click hint */}
            <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              Click to open
            </div>
          </div>
        </div>

        {/* Desktop Preview */}
        <div className="hidden md:flex justify-center">
          <div 
            className="relative bg-white border-2 border-gray-200 rounded-lg shadow-md overflow-hidden cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all duration-200 group"
            onClick={handlePreview}
          >
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 z-10 flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white bg-opacity-90 rounded-full p-3">
                <Eye className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            
            <Document 
              file={company.pitchDeck.url} 
              loading={
                <div className="flex items-center justify-center bg-gray-50 rounded-lg w-[600px] h-[400px]">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <div className="text-sm text-gray-500">Loading preview...</div>
                  </div>
                </div>
              }
              error={
                <div className="flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 w-[600px] h-[400px]">
                  <div className="text-center text-gray-500 p-4">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <div className="text-sm">Preview unavailable</div>
                    <div className="text-xs mt-1">Click to open full PDF</div>
                  </div>
                </div>
              }
            >
              <Page 
                pageNumber={1} 
                width={600}
                renderTextLayer={false} 
                renderAnnotationLayer={false}
                className="shadow-sm"
              />
            </Document>
            
            {/* Click hint */}
            <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              Click to open
            </div>
          </div>
        </div>
        
        {/* Mobile-friendly description */}
        <div className="mt-3 text-center">
          <p className="text-xs text-gray-500">
            Tap preview to open full presentation
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default PitchDeckSection; 